<template>
  <div class="space-y-1">
    <div
      v-for="matchedDiscountCodes in matchedDiscountCodes"
      :key="matchedDiscountCodes.code"
      class="flex justify-between text-sm text-text"
    >
      <span
        >{{ $t('checkout.discount_code') }}:
        <span class="uppercase">{{ matchedDiscountCodes.code }}</span></span
      >
      <span
        >-{{
          formatPrice(matchedDiscountCodes.discount, 'DKK', $i18n.locale)
        }}</span
      >
    </div>

    <div
      v-for="matchedGiftCodes in matchedGiftCodes"
      :key="matchedGiftCodes.code"
      class="flex justify-between text-sm text-text"
    >
      <span
        >{{ $t('checkout.gift_card') }}:
        <span class="uppercase">{{ matchedGiftCodes.code }}</span></span
      >
      <span
        >-{{
          formatPrice(matchedGiftCodes.discount, 'DKK', $i18n.locale)
        }}</span
      >
    </div>

    <div class="flex justify-between text-sm text-text" v-if="totals.fee > 0">
      <span>{{ $t('common.fee') }}</span>
      <span>{{ formatPrice(totals.fee, 'DKK', $i18n.locale) }}</span>
    </div>
    <div class="flex justify-between text-sm text-text" v-if="totals.transaction_fee > 0">
      <span>{{ $t('common.transaction_fee') }}</span>
      <span>{{ formatPrice(totals.transaction_fee, 'DKK', $i18n.locale) }}</span>
    </div>
    <div class="flex justify-between text-sm text-text" v-if="totals.discount > 0">
      <span>{{ $t('common.discount') }}</span>
      <span>{{ formatPrice(-totals.discount, 'DKK', $i18n.locale) }}</span>
    </div>

    <div class="flex justify-between text-lg font-semibold">
      <span>{{ $t('common.total') }}</span>
      <span>{{ formatPrice(totals.total, 'DKK', $i18n.locale) }}</span>
    </div>
    <div class="flex justify-between text-sm text-text" v-if="totals.vat > 0">
      <span>{{ $t('common.vat') }}</span>
      <span>{{ formatPrice(totals.vat, 'DKK', $i18n.locale) }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue';

  import { discountCodes, giftCodes } from '@/data';
  import { useCartStore } from '@/stores/cart';
  import type { CodeItem } from '@/types';
  import { formatPrice } from '@/utils/formatPrice';
  import {
    DISCOUNT_STORAGE_KEY,
    GIFTCARD_STORAGE_KEY,
    loadCodesFromStorage,
  } from '@/utils/loadCodesFromLocalStorage';

  const cart = useCartStore();

  const activeDiscountCodes = ref<CodeItem[]>([]);
  const activeGiftCodes = ref<CodeItem[]>([]);

  const loadCodes = () => {
    activeDiscountCodes.value = loadCodesFromStorage(DISCOUNT_STORAGE_KEY);
    activeGiftCodes.value = loadCodesFromStorage(GIFTCARD_STORAGE_KEY);
  };

  defineExpose({
    loadCodes,
  });

  onMounted(() => {
    loadCodes();
  });

  const matchedDiscountCodes = computed(() => {
    return activeDiscountCodes.value.map(code => {
      const codeItem = discountCodes.find(
        item => item.code.toLowerCase() === code.code.toLowerCase()
      );
      return codeItem || { code: code.code, discount: 0 };
    });
  });

  const matchedGiftCodes = computed(() => {
    return activeGiftCodes.value.map(code => {
      const codeItem = giftCodes.find(
        item => item.code.toLowerCase() === code.code.toLowerCase()
      );
      return codeItem || { code: code.code, discount: 0 };
    });
  });

  const totals = computed(() => {
    const totals = {
      total: 0,
      fee: 0,
      vat: 0,
      transaction_fee: 0,
      discount: 0,
    };

    totals.total = cart.cartTotals.total;
    totals.fee = cart.cartTotals.total_ticketfee;
    totals.vat = cart.cartTotals.total_vat;
    totals.transaction_fee = cart.cartTotals.total_transaction_fee;
    totals.discount = cart.cartTotals.total_discount;

    matchedDiscountCodes.value.forEach(code => {
      if (code.discount) {
        totals.total -= code.discount;
      }
    });

    matchedGiftCodes.value.forEach(code => {
      if (code.discount) {
        totals.total -= code.discount;
      }
    });

    return {
      ...totals
    };
  });
</script>
